/**
 * LiveActivityService - Bridge to iOS Live Activities for Dynamic Island
 * 
 * This service manages the Live Activity widget that displays charging information
 * in the Dynamic Island and on the Lock Screen during an active charging session.
 * 
 * Supports:
 * - Starting a Live Activity when charging begins
 * - Updating the activity in real-time with kWh, time, and cost
 * - Ending the activity when charging completes
 * - Server-side push updates via ActivityKit push tokens
 */

import { NativeModules, Platform } from 'react-native';

// Get the native module - will be undefined on non-iOS or if not properly linked
const { LiveActivityModule } = NativeModules;

// Debug: Log whether the native module was found
if (Platform.OS === 'ios') {
  console.log('[LiveActivity] Native module available:', !!LiveActivityModule);
  if (!LiveActivityModule) {
    console.log('[LiveActivity] Available NativeModules:', Object.keys(NativeModules));
  }
}

// ============================================================================
// Types
// ============================================================================

export interface StartActivityParams {
  /** Name of the charger */
  chargerName: string;
  /** Full address of the charger */
  chargerAddress: string;
  /** Order/session ID */
  orderId: string;
  /** Charging capacity in amps */
  chargingCapacity: number;
  /** Price per kWh (for display) */
  pricePerKwh: number;
}

export interface UpdateActivityParams {
  /** Current kWh charged */
  kwhCharged: number;
  /** Time elapsed in seconds */
  timeElapsedSeconds: number;
  /** Estimated cost so far */
  estimatedCost: number;
  /** Whether actively charging */
  isCharging: boolean;
  /** Human-readable status string */
  chargingStatus: string;
  /** Current charging rate in kW (optional) */
  chargingRateKw?: number;
}

export interface ActivityStartResult {
  /** Unique activity ID */
  activityId: string;
  /** Push token for server-side updates (send to backend) */
  pushToken: string | null;
  /** Whether the activity was successfully started */
  started: boolean;
}

export interface CurrentActivityInfo {
  activityId: string;
  orderId: string;
  chargerName: string;
  isActive: boolean;
  pushToken: string | null;
}

// ============================================================================
// LiveActivityService Class
// ============================================================================

class LiveActivityService {
  private isSupported: boolean | null = null;
  private currentActivityId: string | null = null;
  private currentPushToken: string | null = null;
  private currentOrderId: string | null = null;

  // --------------------------------------------------------------------------
  // Support Check
  // --------------------------------------------------------------------------

  /**
   * Check if Live Activities are supported on this device
   * Requirements:
   * - iOS 16.1+
   * - iPhone 14 Pro or later for Dynamic Island
   * - User has enabled Live Activities in settings
   */
  async checkSupport(): Promise<boolean> {
    // Only supported on iOS
    if (Platform.OS !== 'ios') {
      console.log('[LiveActivity] Not iOS platform, Live Activities not supported');
      return false;
    }

    // Check if native module is available
    if (!LiveActivityModule) {
      console.log('[LiveActivity] Native module not found - may need to rebuild');
      return false;
    }

    // Check cached result
    if (this.isSupported !== null) {
      return this.isSupported;
    }

    try {
      this.isSupported = await LiveActivityModule.isLiveActivitySupported();
      console.log('[LiveActivity] Support check:', this.isSupported);
      return this.isSupported;
    } catch (error) {
      console.log('[LiveActivity] Error checking support:', error);
      this.isSupported = false;
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // Activity Lifecycle
  // --------------------------------------------------------------------------

  /**
   * Start a new Live Activity for a charging session
   * 
   * @param params - Activity configuration
   * @returns Activity info including push token, or null if failed
   */
  async startActivity(params: StartActivityParams): Promise<ActivityStartResult | null> {
    const supported = await this.checkSupport();
    if (!supported) {
      console.log('[LiveActivity] Device does not support Live Activities');
      return null;
    }

    try {
      console.log('[LiveActivity] Starting activity for order:', params.orderId);
      
      const result = await LiveActivityModule.startChargingActivity(
        params.chargerName,
        params.chargerAddress,
        params.orderId,
        params.chargingCapacity,
        params.pricePerKwh
      );

      if (result?.started) {
        this.currentActivityId = result.activityId;
        this.currentPushToken = result.pushToken || null;
        this.currentOrderId = params.orderId;

        console.log('[LiveActivity] Started successfully:', {
          activityId: this.currentActivityId,
          hasPushToken: !!this.currentPushToken,
        });

        return {
          activityId: result.activityId,
          pushToken: result.pushToken || null,
          started: true,
        };
      }

      console.log('[LiveActivity] Start returned but not started');
      return null;
    } catch (error: any) {
      console.error('[LiveActivity] Failed to start:', error.message);
      return null;
    }
  }

  /**
   * Update the current Live Activity with new charging data
   * 
   * @param params - Updated charging data
   * @returns Whether the update was successful
   */
  async updateActivity(params: UpdateActivityParams): Promise<boolean> {
    if (!this.currentActivityId) {
      // Try to find existing activity
      const info = await this.getCurrentActivityInfo();
      if (!info?.isActive) {
        return false;
      }
    }

    if (!LiveActivityModule) {
      return false;
    }

    try {
      await LiveActivityModule.updateChargingActivity(
        params.kwhCharged,
        params.timeElapsedSeconds,
        params.estimatedCost,
        params.isCharging,
        params.chargingStatus,
        params.chargingRateKw ?? 0
      );
      return true;
    } catch (error: any) {
      // Don't log every failed update - might be noisy
      if (error.code !== 'NO_ACTIVITY') {
        console.error('[LiveActivity] Update failed:', error.message);
      }
      return false;
    }
  }

  /**
   * End the Live Activity gracefully
   * Shows final stats for a few minutes before dismissing
   * 
   * @param finalKwh - Total kWh charged
   * @param finalCost - Total cost
   * @param totalTimeSeconds - Total charging time in seconds
   */
  async endActivity(
    finalKwh: number,
    finalCost: number,
    totalTimeSeconds: number
  ): Promise<boolean> {
    if (!LiveActivityModule) {
      return false;
    }

    try {
      console.log('[LiveActivity] Ending activity with final stats:', {
        finalKwh,
        finalCost,
        totalTimeSeconds,
      });

      await LiveActivityModule.endChargingActivity(
        finalKwh,
        finalCost,
        totalTimeSeconds
      );

      // Clear local state
      this.currentActivityId = null;
      this.currentPushToken = null;
      this.currentOrderId = null;

      console.log('[LiveActivity] Activity ended successfully');
      return true;
    } catch (error: any) {
      console.error('[LiveActivity] Failed to end activity:', error.message);
      return false;
    }
  }

  /**
   * End all Live Activities immediately
   * Useful for cleanup on app launch or logout
   */
  async endAllActivities(): Promise<boolean> {
    if (!LiveActivityModule) {
      return true; // Nothing to end
    }

    try {
      await LiveActivityModule.endAllActivities();
      
      // Clear local state
      this.currentActivityId = null;
      this.currentPushToken = null;
      this.currentOrderId = null;

      console.log('[LiveActivity] All activities ended');
      return true;
    } catch (error: any) {
      console.error('[LiveActivity] Failed to end all activities:', error.message);
      return false;
    }
  }

  // --------------------------------------------------------------------------
  // Status & Info
  // --------------------------------------------------------------------------

  /**
   * Get information about the current activity
   */
  async getCurrentActivityInfo(): Promise<CurrentActivityInfo | null> {
    if (!LiveActivityModule) {
      return null;
    }

    try {
      const info = await LiveActivityModule.getCurrentActivityInfo();
      if (info) {
        // Update local state
        this.currentActivityId = info.activityId;
        this.currentOrderId = info.orderId;
        this.currentPushToken = info.pushToken || null;
        return info;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if there's an active Live Activity
   */
  isActive(): boolean {
    return this.currentActivityId !== null;
  }

  /**
   * Get the push token for server-side updates
   */
  getPushToken(): string | null {
    return this.currentPushToken;
  }

  /**
   * Get the current order ID associated with the activity
   */
  getCurrentOrderId(): string | null {
    return this.currentOrderId;
  }

  /**
   * Check if the current activity matches the given order
   */
  isActivityForOrder(orderId: string): boolean {
    return this.currentOrderId === orderId;
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Singleton instance of the LiveActivityService
 * Use this for all Live Activity operations
 */
export const liveActivityService = new LiveActivityService();

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format seconds to HH:MM:SS for display
 */
export function formatTimeForActivity(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Calculate charging rate in kW from amps and voltage
 */
export function calculateChargingRateKw(amps: number, voltage: number = 220): number {
  return (amps * voltage) / 1000;
}

export default liveActivityService;

